import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ScanFace, CalendarDays, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, rupiah } from "@/lib/api";
import { PhotoGrid } from "@/components/PhotoCard";
import { ShareButton } from "@/components/ShareButton";
import { DateFolders } from "@/components/DateFolders";
import { KindBadge } from "@/pages/Events";

export default function EventDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [date, setDate] = useState(null);
  const sentinel = useRef();
  const dateParam = date ? { date } : {};

  useEffect(() => {
    api.get(`/events/${id}`, { params: { page: 1, limit: 60, ...dateParam } }).then((r) => { setData(r.data); setPhotos(r.data.photos); setPage(1); }).catch(() => setData(false));
  }, [id, date]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMore = useCallback(async () => {
    if (loadingMore || !data?.has_more) return;
    setLoadingMore(true);
    const { data: d } = await api.get(`/events/${id}`, { params: { page: page + 1, limit: 60, ...dateParam } });
    setPhotos((prev) => [...prev, ...d.photos]);
    setData((prev) => ({ ...prev, has_more: d.has_more }));
    setPage(page + 1);
    setLoadingMore(false);
  }, [id, page, data, loadingMore, date]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!sentinel.current) return;
    const io = new IntersectionObserver((entries) => entries[0].isIntersecting && loadMore(), { rootMargin: "600px" });
    io.observe(sentinel.current);
    return () => io.disconnect();
  }, [loadMore]);

  if (data === null) return <p className="p-16 text-zinc-500">Memuat...</p>;
  if (!data) return <p className="p-16 text-zinc-500" data-testid="event-not-found">Event tidak ditemukan.</p>;
  const { event, total } = data;

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12 fade-up">
        <div>
          <Link to={`/studio/${event.photographer_id}`} className="inline-block text-xs uppercase tracking-widest text-zinc-400 font-bold mb-2 hover:text-blue-600" data-testid="event-photographer-link">{event.photographer_name}</Link>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="event-title">{event.title}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-zinc-500">
            <KindBadge kind={event.kind} testId="event-kind-badge" />
            {event.date && <span className="inline-flex items-center gap-1"><CalendarDays className="h-4 w-4" />{event.date}</span>}
            {event.location && <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" />{event.location}</span>}
            <span data-testid="event-photo-total">{total} foto</span>
          </div>
          {(event.packages?.length > 0 || event.bundle_price > 0) && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid="event-packages">
              {(event.packages || []).map((p) => (
                <span key={p.id} className="rounded-full bg-blue-50 text-blue-700 px-3 py-1 text-sm font-semibold" data-testid={`event-package-${p.count}`}>Paket {p.count} foto {rupiah(p.price)}</span>
              ))}
              {event.bundle_price > 0 && <span className="rounded-full bg-blue-600 text-white px-3 py-1 text-sm font-semibold" data-testid="event-bundle-badge">Semua foto wajahmu {rupiah(event.bundle_price)}</span>}
            </div>
          )}
          {event.description && <p className="mt-4 text-zinc-600 max-w-2xl">{event.description}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          <ShareButton event={event} />
          <Button asChild className="rounded-xl bg-blue-600 hover:bg-blue-700 h-11" data-testid="event-face-search-button">
            <Link to={`/cari?event=${event.id}`}><ScanFace className="mr-2 h-4 w-4" />Cari wajahku di event ini</Link>
          </Button>
        </div>
      </div>
      <DateFolders eventId={id} value={date} onChange={setDate} testPrefix="event-date-folder" />
      <PhotoGrid photos={photos} empty="Belum ada foto di event ini." />
      <div ref={sentinel} className="h-10" />
      {loadingMore && <p className="text-center text-sm text-zinc-500" data-testid="event-loading-more">Memuat foto lainnya...</p>}
    </main>
  );
}
